import {
  POPOVER_LIST_ROOT_ATTR,
  POPOVER_LIST_SEARCH_ATTR,
} from "./popover-list-search-attrs";

const OPEN_SEARCH_SELECTOR = `[${POPOVER_LIST_ROOT_ATTR}]:not([data-state="closed"]) [${POPOVER_LIST_SEARCH_ATTR}]:not([disabled])`;

/** Windows with the capture listener (this tab + pop-out windows rendered from it). */
const redirectWindows: WeakSet<Window> = new WeakSet();

function isListSearchField(
  el: EventTarget | null,
): el is HTMLInputElement | HTMLTextAreaElement {
  if (typeof Element === "undefined") return false;
  if (!(el instanceof Element)) return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA";
}

/**
 * Visible list-search field in an open overlay, or null.
 */
export function findOpenPopoverListSearchInput(
  doc: Document | null = typeof document === "undefined" ? null : document,
): HTMLInputElement | HTMLTextAreaElement | null {
  if (doc == null) return null;
  const el = doc.querySelector(OPEN_SEARCH_SELECTOR);
  return isListSearchField(el) ? el : null;
}

/** True while a searchable overlay list is mounted and open. */
export function isOpenPopoverListSearchActive(): boolean {
  return findOpenPopoverListSearchInput() != null;
}

function isTypingSurfaceInsideOverlay(
  el: EventTarget | null,
  overlayRoot: Element | null,
): boolean {
  if (!(el instanceof HTMLElement) || overlayRoot == null) return false;
  if (!overlayRoot.contains(el)) return false;
  const tag: string = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return el.closest("[contenteditable]:not([contenteditable='false'])") != null;
}

function nativeValueSetter(
  input: HTMLInputElement | HTMLTextAreaElement,
): ((value: string) => void) | undefined {
  if (typeof HTMLInputElement === "undefined") return undefined;
  const proto =
    input.tagName === "INPUT"
      ? HTMLInputElement.prototype
      : HTMLTextAreaElement.prototype;
  return Object.getOwnPropertyDescriptor(proto, "value")?.set;
}

function insertTextInInput(
  input: HTMLInputElement | HTMLTextAreaElement,
  text: string,
): void {
  const setter = nativeValueSetter(input);
  if (setter == null) return;
  input.focus({ preventScroll: true });
  const start: number = input.selectionStart ?? input.value.length;
  const end: number = input.selectionEnd ?? input.value.length;
  setter.call(
    input,
    input.value.slice(0, start) + text + input.value.slice(end),
  );
  input.dispatchEvent(new Event("input", { bubbles: true }));
  const caret: number = start + text.length;
  input.setSelectionRange(caret, caret);
}

function deleteBackwardInInput(
  input: HTMLInputElement | HTMLTextAreaElement,
): void {
  const setter = nativeValueSetter(input);
  if (setter == null) return;
  input.focus({ preventScroll: true });
  const start: number = input.selectionStart ?? input.value.length;
  const end: number = input.selectionEnd ?? input.value.length;
  if (start === 0 && start === end) return;
  const nextStart: number = start === end ? start - 1 : start;
  setter.call(input, input.value.slice(0, nextStart) + input.value.slice(end));
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.setSelectionRange(nextStart, nextStart);
}

function consumeKey(event: KeyboardEvent): void {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

function onWindowKeyDown(event: KeyboardEvent): void {
  if (event.defaultPrevented) return;
  if (event.isComposing || event.keyCode === 229) return;
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  // The window the key was typed in (a pop-out composer has its own).
  const search = findOpenPopoverListSearchInput(
    event.view?.document ?? undefined,
  );
  if (search == null) return;
  if (event.target === search || search.contains(event.target as Node)) return;
  const overlayRoot = search.closest(`[${POPOVER_LIST_ROOT_ATTR}]`);
  // Composer / page editors keep focus on open (`modal={false}`). Keys still
  // belong in the overlay search. Only skip a second field inside the overlay.
  if (isTypingSurfaceInsideOverlay(event.target, overlayRoot)) return;

  if (event.key.length === 1) {
    consumeKey(event);
    insertTextInInput(search, event.key);
    return;
  }
  if (event.key === "Backspace") {
    consumeKey(event);
    deleteBackwardInInput(search);
  }
}

/**
 * Installs one window-capture listener per window (this tab, pop-out
 * windows) that types into the open list search when caret focus drifted to
 * the trigger or a menu item.
 */
export function ensurePopoverListSearchKeyRedirect(
  targetWindow: Window | null = typeof window === "undefined" ? null : window,
): void {
  if (targetWindow == null) return;
  if (redirectWindows.has(targetWindow)) return;
  redirectWindows.add(targetWindow);
  targetWindow.addEventListener("keydown", onWindowKeyDown, { capture: true });
}
